
import { ANSWER_GENERATION_TYPE, USER_TYPE } from "../../@core/models/enums";
import { Document, IAIStep, IAttachment, IDocumentVersion, IFile, IOrganization, IProject, IShareUser, IStatus, IUser, IWorkspace, SHARE_ACCESS } from "../../@core/models/model";
import { CONTRACT_STATUS } from "../../@shared/components/badge/badge.enums";
import { CONTRACT_APPROVAL_STATUS } from "../../@shared/components/user-chips/user-chips.component";
import { DOCUMENT_CREATION_SOURCE, IContractResponse } from "../services/contracts.service";
import { IFormMetrics } from "../services/document-form.service";
import { IKnowledgeBaseStore } from "../services/knowledgebase.service";
import { IQnaPair } from "../services/qna-pair.service";
import { COMPARE_DOCUMENT_STATUS, IConstructionAttachment, IDocumentType } from "./settings.model";

export enum PENDING_STATUS {
    OVERDUE = 'overdue',
    PENDING_APPROVALS = 'pendingApprovals',
    PENDING_SIGNATURES = 'pendingSignatures'
}

export enum LOGIN_METHOD {
    PASSWORD = 'PASSWORD',
    GOOGLE = 'GOOGLE',
    MICROSOFT = 'MICROSOFT',
}


export enum CREATE_CONTRACT_TYPES {
    EMPTY = 'EMPTY',
    UPLOAD = 'UPLOAD',
    GENERATE = 'GENERATE'
}

export enum CONTRACT_TYPE {
    ALL = 'ALL',
    DRAFTED = 'DRAFTED',
    SHARED = 'SHARED',
}

export enum CONTRACT_DOCUMENTS {
    ALL = 'All Documents',
    DRAFTED = 'Drafted Documents',
    SHARED = 'Shared Documents'
}

export enum CONTRACT_GENERATION_STATUS {
    COMPLETED = 'COMPLETED',
    IN_PROGRESS = 'IN_PROGRESS',
}
export enum KNOWLEDGE_SCOPE {
    WORKSPACE = 'WORKSPACE',
    PROJECT = 'PROJECT'
}

export enum IContractRole {
    OWNER = 'OWNER',
    CONTRACTOR = 'CONTRACTOR',
    NONE = 'NONE'
}

export const enum PRODUCT_WALKTHROUGH_TYPE {
    HOME = 'HOME',
    SETTINGS = 'SETTINGS',
    EDITOR = 'EDITOR',
    QUESSTIONAIRE = 'QUESSTIONAIRE',
    COMPARE = 'COMPARE',
    PROJECT_DASHBOARD = 'PROJECT_DASHBOARD',
    PROMPT_LIBRARY = 'PROMPT_LIBRARY'
}

interface IDashboardTab {
    label: string;
    icon: string;
    key: string;
    count: number;
}

export enum DASHBOARD_SCREEN {
    DOCUMENTS = 'DOCUMENTS',
    PROJECTS = 'PROJECTS',
    ARCHIVED = 'ARCHIVED'
}

export interface IContract {
    readonly _id: string
    organization: string | IOrganization,
    workspace: string | IWorkspace,
    attachment: string | IAttachment['_id'] | IAttachment,
    name: string,
    description: string,
    checkpoint: string,
    users: {
        user: string | IUser['_id'] | IUser,
        role: string
    }[],
    status: CONTRACT_STATUS,
    documentStatus: string | IStatus | IStatus['_id'],
    createdBy: string | IUser['_id'] | IUser,
    defaultSignature: SIGNATORY_TYPE,
    docusignId: string,
    adobeSignId: string,
    updatedAt: string,
    createdAt: string,
    summary?: string,
    currentUserRole?: SHARE_ACCESS[], // Not saved in model, this field comes on fetching contracts
    linkedDocType: IDocumentType
    reviewedBy: string[]
    dueDate?: string | Date
    dueDateExceeding?: boolean,
    project?: string | IProject
    containsForm?: boolean
    generationStatus?: CONTRACT_GENERATION_STATUS,
    source?: DOCUMENT_CREATION_SOURCE,
}

export interface IProjectDocument extends IContractResponse {
    metrics?: IFormMetrics,
    isPinned?: boolean,
}

export interface IContractSection {
    title: string;
    content: string;
}

export interface IContractInsights extends Document {
    summary: string | IAttachment | IAttachment['_id'];
    customSummary?: string | IAttachment | IAttachment['_id'];
    documentInSync: boolean
    contract: IContract | string | IContract['_id'];
    createdBy: Partial<IUser> | string | IUser['_id'];
    createdAt: Date;
    updatedAt: Date;
}

export interface IContractApprovalConf extends Document {
    contract: string | IContract,
    approvers: IShareUser[],
    createdBy: IUser,
    status: CONTRACT_APPROVAL_STATUS,
}

export interface IContractSignatoryConf extends Document {
    contract: string | IContract['_id'] | IContract,
    signatories: {
        email: string,
        name: string,
        status: string,
        signedAttachment?: string,
        updatedAt: string,
        user: string | IUser,
        type: USER_TYPE
    }[],
    createdBy: IUser,
    status: string,
}

export enum SIGNATORY_TYPE {
    DOCU_SIGN = 'DOCU_SIGN',
    ADOBE_SIGN = 'ADOBE_SIGN',
}

export enum DOCUMENT_MODEL {
    CONTRACT = 'Contract',
    REPOSITORY = 'File',
    VERSION = 'Version',
    LINKED_DOCUMENT = 'LinkedDocument',
    UPLOADED_DOCUMENT = 'UploadedDocument',
    ATTACHMENT = 'Attachment'
}

export enum KPI_TYPE {
    STANDARD = 'STANDARD',
    CUSTOM = 'CUSTOM'
}

export enum KPI_DATA_TYPE {
    'TEXT' = 'TEXT',
    'TABLE' = 'TABLE',
    'GRAPH' = 'GRAPH',
}

export enum CUSTOMISATION_TONE {
    NEUTRAL = 'Neutral',
    FORMAL = 'Formal',
    TECHNICAL = 'Technical'
}

export enum VERBOSITY {
    CONCISE = 'Concise',
    MODERATE = 'Moderate',
    DETAILED = 'Detailed'
}

export interface IStandardDocumentType extends Document {
    name: string,
    description: string,
}

export interface IKpi extends Document {
    name: string,
    description: string,
    kpi_type: KPI_TYPE,
    data_type: KPI_DATA_TYPE,
    docType: string | IStandardDocumentType
}

export enum EMBEDDINGS_GENERATION_STATUS {
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED'
}
export interface IDocumentCompare extends Document {
    organization: string | IOrganization;
    name: string;
    contract: string | IContract;
    compareContracts: {
        _id: string,
        docModel: DOCUMENT_MODEL,
        doc: null | IAttachment | IContract | IFile | IDocumentVersion,
        embeddingsStatus: EMBEDDINGS_GENERATION_STATUS
    }[]
    compare: string | IAttachment;
    kpis: {
        kpi: string | IKpi,
        comparison: string | IAttachment,
        generationTime?: number // in seconds,
        thoughts: string[],
        plan: string[] | IAIStep[],
        status: COMPARE_DOCUMENT_STATUS,
        /** Why the KPI was paused; 'credits_exhausted' triggers the credits-overview UI. */
        pauseReason?: string | null,
    }[]
    documentInSync: boolean;
    status: {
        documents: string[],
        linkedDocuments: string[],
        summary: COMPARE_DOCUMENT_STATUS,
        kpis: COMPARE_DOCUMENT_STATUS
    }
    promptSuggestions: string[];
    createdBy: string;
    createdAt: Date;
    updatedAt: Date;
}

export enum QUESTION_TYPE {
    TEXT = 'TEXT',
    SINGLE_SELECT = 'SINGLE_SELECT',
    MULTIPLE_SELECT = 'MULTIPLE_SELECT',
    RADIO = 'RADIO',
    CHECKBOX = 'CHECKBOX',
    TABLE = 'TABLE',
    EMAIL = 'EMAIL',
    ATTACHMENT = 'ATTACHMENT',
    OTHER = 'OTHER',
}

export enum DUPLICATE_CHECK_STATUS {
    PROCESSED = 'PROCESSED',
    UNPROCESSED = 'UNPROCESSED'
}

export interface IDuplicateCluster extends Document {
    organization: string | IOrganization;
    questions: (string | IFormQuestion)[];
}

export enum DELETE_TYPE {
    QUESTION = 'Question',
    SECTION = 'Section'
}

export interface IFormQuestion extends Document {
    organization: string | IOrganization;
    documentForm: string | IDocumentForm;
    title: string;
    required: boolean;
    type: QUESTION_TYPE;
    options: { label: string, value: string }[];
    assignedTo: {
        user: IUser,
        status: FORM_QUESTION_STATUS,
        email?: string,
        name?: string,
        type?: USER_TYPE,
        modifiedAt?: Date
    }[];
    reviewedBy: string | IUser;
    cells: string[][];
    status: FORM_QUESTION_STATUS;
    statusUpdatedBy: string | IUser;
    linkedQnAPair: string | IQnaPair;
    duplicateClusterId: string | IDuplicateCluster;
    duplicateCheckStatus: DUPLICATE_CHECK_STATUS;
    markedNotDuplicateClusterIds: (string | IDuplicateCluster)[];
    watchers?: string[] | IUser[];
    isWatcher: boolean;
    canViewWatchers: boolean;
    generationOptions: {
        prompt: string,
        qnaBank: boolean,
        repository: boolean
    }
    createdAt: Date;
    updatedAt: Date;
}

export enum FORM_QUESTION_STATUS {
    PENDING = 'PENDING',
    FLAGGED = 'FLAGGED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    REQUESTED = 'REQUESTED',
}

export enum ANSWER_GENERATION_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    EXTRACTED = 'EXTRACTED',
    NOT_EXTRACTED = 'NOT_EXTRACTED' //Not extracted means either pending or in progress, added for ease of use to only use in frontend
}

export interface IFormAnswer extends Document {
    organization: string | IOrganization;
    documentForm: string | IDocumentForm;
    question: string | IFormQuestion;
    value: string;
    cells: string[][];
    status: ANSWER_GENERATION_STATUS;
    sources: {
        file: IFile;
        content: string;
        qnaPair: IQnaPair
    }[];
    metadata: {
        confidence: number;
        assessment: string;
    };
    updatedBy: string | IUser;
    attachments: IConstructionAttachment[]; // for attachment question type
    answeredBy?: ANSWER_GENERATION_TYPE; // Indicates whether the answer was generated by AI or USER
}

export enum DOCUMENT_FORM_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    EXTRACTED = 'EXTRACTED',
    /** Credits exhausted or unrecoverable error during extraction or answering. */
    FAILED = 'FAILED',
}

export interface IDocumentForm extends Document {
    organization: string | IOrganization;
    contract: string | IContract;
    useQnaPair: boolean;
    knowledgeBase: string[] | IKnowledgeBaseStore[];
    files: string[] | IFile[];
    instructions: string;
    tone: CUSTOMISATION_TONE;
    verbosity: VERBOSITY;

    sections: {
        _id: string,
        title: string;
        form: {
            question: IFormQuestion;
            answer: IFormAnswer;
            _id: string;
        }[]
    }[];

    status: {
        questions: DOCUMENT_FORM_STATUS;
        answers: DOCUMENT_FORM_STATUS;
    },

    createdBy: string | IUser;
    createdAt: Date;
    updatedAt: Date;
}


export interface IContractComment extends Document {
    builder: string | IOrganization,
    category: string | IContract,
    user: IUser
    content: string
    replies: {
        user: IUser
        content: string,
        createdAt: Date
    }[],
    createdAt: Date,
    updatedAt: Date
}
